// Auth state — identical pattern to apps/web/src/auth/AuthProvider.tsx.
// Hub shares the same API and cookie domain; a valid merch'd session works
// on both app.getmerchd.com and hub.getmerchd.com without re-login.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, bindAuth } from "@/lib/api";
import { makeSessionRefresher, type RefreshOutcome } from "@/lib/session-refresh";

export interface SessionUser {
  sub: string;
  email: string;
  name: string;
  roles: string[];
}

/** Two-step sign-in (2026-10-21): password right, code still owed. The
 *  token opens only /auth/mfa/* and lasts 10 minutes. Setting two-step up
 *  happens in Flow; the hub only asks for the code. */
export interface TwoStepChallenge {
  token: string;
  setup: boolean;
}

export type LoginOutcome =
  | { kind: "signed_in" }
  | { kind: "two_step"; challenge: TwoStepChallenge };

interface AuthState {
  user: SessionUser | null;
  /** Page load couldn't reach the server (deploy / outage) and keeps
   *  retrying — RequireAuth shows "Can't reach … — retrying…", never /login. */
  unreachable: boolean;
  /** The Retry button: try again now instead of after the current wait. */
  retryNow: () => void;
  /** Gave up retrying (MAX_UNREACHABLE_CYCLES rounds, or the server said
   *  something retrying won't fix): show `unreachableMessage` + "Sign in
   *  again" instead of retrying forever. */
  unreachableGaveUp: boolean;
  unreachableMessage: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** Adopt the session /auth/mfa/verify returned. */
  adoptSession: (accessToken: string, sessionUser: SessionUser) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Rounds of renewal retries on page load before offering "Sign in again". */
const MAX_UNREACHABLE_CYCLES = 3;

/** One renewer per page: every caller shares the same in-flight renewal. */
const renewSession = makeSessionRefresher<SessionUser>({ url: "/auth/refresh" });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const [unreachableGaveUp, setUnreachableGaveUp] = useState(false);
  const [unreachableMessage, setUnreachableMessage] = useState<string | null>(null);
  const accessTokenRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // A fresh sign-in or sign-out ends any "can't reach" state from page load.
  const clearUnreachable = useCallback(() => {
    setUnreachable(false);
    setUnreachableGaveUp(false);
    setUnreachableMessage(null);
  }, []);

  const handleLogout = useCallback(() => {
    clearUnreachable();
    accessTokenRef.current = null;
    setUser(null);
    navigate("/login");
  }, [navigate, clearUnreachable]);

  // Renew from the refresh cookie: single flight, retried through deploys,
  // "signed out" only on a definitive 401 (lib/session-refresh.ts).
  const refresh = useCallback(async (): Promise<RefreshOutcome<SessionUser>> => {
    const outcome = await renewSession();
    if (outcome.kind === "ok") accessTokenRef.current = outcome.accessToken;
    return outcome;
  }, []);

  useEffect(() => {
    bindAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshToken: refresh,
      onLogout: handleLogout,
    });
  }, [refresh, handleLogout]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep trying until the server gives a clear answer. A deploy or an
      // outage shows "Can't reach … — retrying…" (RequireAuth) instead of
      // the sign-in page; the cookie is kept throughout.
      let outcome = await refresh();
      let cycles = 0;
      while (!cancelled && outcome.kind === "unavailable") {
        cycles++;
        setUnreachable(true);
        setUnreachableMessage(outcome.message ?? null);
        // A way out: after a few rounds (~1 min each), or at once when the
        // server says something retrying won't fix, offer "Sign in again".
        if (outcome.definitive || cycles >= MAX_UNREACHABLE_CYCLES) {
          setUnreachableGaveUp(true);
          setLoading(false);
          return;
        }
        outcome = await refresh();
      }
      if (cancelled) return;
      setUnreachable(false);
      if (outcome.kind !== "ok") {
        setLoading(false); // definitively signed out → the sign-in page
        return;
      }
      try {
        // The renewal already says who this is; /me only as a fallback.
        const sessionUser =
          outcome.user ?? (await api<{ user: SessionUser }>("/auth/me")).user;
        if (!cancelled) setUser(sessionUser);
      } catch {
        accessTokenRef.current = null;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string): Promise<LoginOutcome> => {
    const body = await api<
      | { access_token: string; user: SessionUser }
      | { mfa_required: true; mfa_token: string; setup: boolean }
    >("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      skipAuthRefresh: true,
      skipAuth: true,
    });
    if ("mfa_required" in body && body.mfa_required === true) {
      return { kind: "two_step", challenge: { token: body.mfa_token, setup: body.setup === true } };
    }
    const session = body as { access_token: string; user: SessionUser };
    clearUnreachable();
    accessTokenRef.current = session.access_token;
    setUser(session.user);
    return { kind: "signed_in" };
  }, []);

  const adoptSession = useCallback((accessToken: string, sessionUser: SessionUser) => {
    clearUnreachable();
    accessTokenRef.current = accessToken;
    setUser(sessionUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // already gone
      } else {
        console.warn("[auth/logout] server call failed", err);
      }
    } finally {
      handleLogout();
    }
  }, [handleLogout]);

  return (
    <AuthContext.Provider value={{ user, loading, unreachable, unreachableGaveUp, unreachableMessage, retryNow: renewSession.retryNow, login, adoptSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
